import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import { getUniqueWalkinWalkout } from 'api';

const DualBarChart = () => {
  const [chartData, setChartData] = useState([]);
  const currentDate = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [error, setError] = useState(null);
  /* eslint-disable-next-line */
  const [footfalldata, setFootfalldata] = useState(0);
  /* eslint-disable-next-line */
  const [empFtFallCount, setEmpFtFallCount] = useState(0);
  /* eslint-disable-next-line */
  const [attended, setAttended] = useState(0);
  /* eslint-disable-next-line */
  const [unAttended, setUnAttended] = useState(0);
  const source = JSON.parse(localStorage.getItem('source'));

  const formatDate = (date) => {
    if (!date) return '';

    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date;
      }
      if (date.match(/^\d{1,2}-\d{1,2}-\d{2}$/)) {
        const [day, month, shortYear] = date.split('-');
        const year = `20${shortYear}`; // Assuming 21st century
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return date;
    }

    if (date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return '';
  };

  const formattedDate = formatDate(currentDate);
  console.log('Formatted date:', formattedDate);
  console.log('Selected time:', selectedTime);

  async function getInteractionNumbers(commonBody, signal) {
    try {
      const startTime = new Date(selectedTime.start_time).toUTCString();
      const endTime = new Date(selectedTime.end_time).toUTCString();

      console.log('API Request Data:', {
        date: formattedDate,
        source_id: source,
        start_time: startTime,
        end_time: endTime
      });

      const requestData = {
        date: formattedDate,
        source_id: source,
        start_time: startTime,
        end_time: endTime
      };

      const response = await getUniqueWalkinWalkout(requestData);
      const data = response.data.data[0]?.footfall;

      if (!signal.aborted) {
        if (!data || !data.zoneFootfall || data.zoneFootfall.length === 0) {
          setAttended(0);
          setUnAttended(0);
          setIsDataAvailable(false);
          setIsLoading(false);
        } else {
          const totalAttended = data.total_attended;
          const totalUnAttended = data.total_unattended;

          const zoneWise = data.zoneFootfall.map((zone) => {
            const footfall = zone.footfall || 0;
            const attended = zone.attended || 0;
            // Ensure not_interacted is never negative
            const not_interacted = Math.max(0, footfall - attended);
            // Calculate percentages
            const total = attended + not_interacted;
            const attendedPercentage = total > 0 ? (attended / total) * 100 : 0;
            const notInteractedPercentage = total > 0 ? (not_interacted / total) * 100 : 0;

            return {
              zone_name: zone.zone_name,
              footfall: footfall,
              customerStaff: attended,
              not_interacted: not_interacted,
              attendedPercentage: attendedPercentage,
              notInteractedPercentage: notInteractedPercentage,
              total: total
            };
          });

          setAttended(totalAttended);
          setUnAttended(totalUnAttended);
          setChartData(zoneWise);
          setIsDataAvailable(true);
          setIsLoading(true);

          // Also set the footfall data
          setFootfalldata(data.store_footfall);
          setEmpFtFallCount(data.employee_footfall || 0);
        }
      }
    } catch (error) {
      if (!signal.aborted) {
        setAttended(0);
        setUnAttended(0);
        setChartData([]);
        setIsDataAvailable(false);
        setIsLoading(false);
        setError('Failed to fetch interaction data');
        console.error('Error fetching interaction numbers:', error);
      }
    }
  }

  useEffect(() => {
    if (!currentDate) return;

    const controller = new AbortController();
    const { signal } = controller;

    setIsLoading(true);
    setError(null);

    getInteractionNumbers(formattedDate, signal).finally(() => {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    });

    return () => {
      controller.abort();
    };
  }, [currentDate, formattedDate, selectedTime?.start_time_string, selectedTime?.end_time_string]); // Added time dependencies

  if (!currentDate) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#6c757d', fontSize: '16px' }}>Please select a date to view footfall data</p>
      </div>
    );
  }

  // if (isLoading) {
  //   return (
  //     <div
  //       style={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '480px',
  //         backgroundColor: '#f8f9fa',
  //         border: '1px solid #e9ecef',
  //         borderRadius: '8px'
  //       }}
  //     >
  //       <p style={{ color: '#6c757d', fontSize: '16px' }}>Loading footfall data...</p>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#dc3545', fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="chart-loading-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <div
          className="spinner"
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderLeftColor: '#44BE54',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}
        ></div>
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div style={{ fontWeight: '500', color: '#555' }}>Loading Interaction Data...</div>
      </div>
    );
  }

  if (!isDataAvailable) {
    const timeRange =
      selectedTime?.start_time_string && selectedTime?.end_time_string
        ? ` (${selectedTime.start_time_string} - ${selectedTime.end_time_string})`
        : '';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#6c757d', fontSize: '16px', marginBottom: '8px' }}>
          No footfall data available for {formattedDate}
          {timeRange}
        </p>
      </div>
    );
  }

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false }
    },
    xaxis: {
      categories: chartData.map((item) => item.zone_name),
      title: {
        text: 'Departments'
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px'
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 7,
        borderRadiusApplication: 'end',
        columnWidth: '55%',
        barGap: 10
      }
    },
    stroke: {
      show: true,
      width: 6,
      colors: ['transparent']
    },
    dataLabels: { enabled: false },
    legend: { position: 'top' },
    colors: ['#44BE54', '#FB1751'],
    yaxis: {
      title: { text: 'Percentage (%)' },
      max: 100
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val, { seriesIndex, dataPointIndex }) {
          const rawValue = seriesIndex === 0 ? chartData[dataPointIndex].customerStaff : chartData[dataPointIndex].not_interacted;
          return `${val.toFixed(1)}% (${rawValue} visits)`;
        }
      }
    }

    // annotations: {
    //   yaxis: [
    //     {
    //       y: footfalldata,
    //       borderColor: '#00E396',
    //       label: {
    //         borderColor: '#00E396',
    //         style: {
    //           color: '#fff',
    //           background: '#00E396'
    //         },
    //         text: `Avg: ${footfalldata}`
    //       }
    //     }
    //   ]
    // }
  };

  const series = [
    {
      name: 'Interacted',
      data: chartData.map((item) => parseFloat(item.attendedPercentage.toFixed(1)))
    },
    {
      name: 'Not Interacted',
      data: chartData.map((item) => parseFloat(item.notInteractedPercentage.toFixed(1)))
    }
  ];

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ padding: '8px 16px', backgroundColor: '#f0f8ff', borderRadius: '4px', flex: 1 }}>
          <div className="flex items-center gap-2">
            <DirectionsWalkIcon className="text-[#2D9BBC]" />
            <div>
              <p style={{ margin: 0, fontWeight: 'bold' }}>Total Footfall: {footfalldata}</p>
            </div>
          </div>
        </div>
        <div style={{ padding: '8px 16px', backgroundColor: '#f0fff0', borderRadius: '4px', flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Interacted: {attended}</p>
        </div>
        <div style={{ padding: '8px 16px', backgroundColor: '#fff0f0', borderRadius: '4px', flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Not Interacted: {unAttended}</p>
        </div>
      </div> */}
      <div>
        <Chart options={options} series={series} type="bar" height={480} />
      </div>
    </div>
  );
};

export default DualBarChart;
